import React, { useState, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppConfig } from "../../../constants";
import { SIGNUP_COLORS } from "../../../pages/public/SignUp/tokens";
import StepIndicator, { StepIndicatorProps } from "../StepIndicator";

const LOGO = AppConfig.appLogo2;

export interface LeftSideOnboardingProps {
  steps: Omit<StepIndicatorProps, "onClick" | "isLast">[];
  onStepClick?: (stepNumber: number) => void;
  variant?: "video" | "image";
  videoSrc?: string;
  imageSrc?: string;
  showIntroCopy?: boolean;
  isComplete?: boolean;
}

const LeftSideOnboarding: React.FC<LeftSideOnboardingProps> = ({
  steps,
  onStepClick,
  variant = "video",
  videoSrc = "https://shipeezi-images-dev.s3.ap-southeast-2.amazonaws.com/world_globe_video.mp4",
  imageSrc,
  showIntroCopy = true,
  isComplete = false,
}) => {
  const { t: translate } = useTranslation();
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    const ids = [200, 400, 600, 800].map((d, i) =>
      setTimeout(() => setAnimationStage(i + 1), d)
    );
    return () => ids.forEach(clearTimeout);
  }, []);

  const getAnimationStyle = (stage: number) => ({
    opacity: animationStage >= stage ? 1 : 0,
    transform: animationStage >= stage ? "translateY(0)" : "translateY(20px)",
    transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  });

  const isVideo = variant === "video";
  const isCompact = !showIntroCopy;
  const GAP_UNITS = 3;

  return (
    <Box position="relative" height="100%" width="100%" overflow="hidden">
      {isVideo ? (
        <video
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: "absolute",
            top: 0,
            left: "-25%",
            width: "130%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "55% center",
            zIndex: 1,
            transform: "scaleX(-1)",
          }}
        />
      ) : (
        imageSrc && (
          <Box
            component="img"
            src={imageSrc}
            alt="Background"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 1,
            }}
          />
        )
      )}

      <Box sx={{ position: "absolute", inset: 0, zIndex: 2 }} />

      <Stack
        direction="column"
        height="100%"
        justifyContent={showIntroCopy ? "center" : "flex-start"}
        alignItems="flex-start"
        sx={{
          position: "relative",
          zIndex: 3,
          color: SIGNUP_COLORS.white,
          px: { xs: 4, lg: 6 },
          pt: isCompact ? "65px" : 0,
          maxWidth: isCompact ? "280px" : "unset",
        }}
      >
        <Box sx={{ ...getAnimationStyle(1), mb: isCompact ? "24px" : "40px" }}>
          <Box
            component="img"
            src={LOGO}
            alt="Shipeezi"
            sx={{ width: 196, height: 40, display: "block" }}
          />
        </Box>

        {!isCompact && (
          <Stack spacing={3} sx={{ mb: "40px" }}>
            <Box sx={getAnimationStyle(2)}>
              <Typography
                fontSize="36px"
                lineHeight="44px"
                fontWeight={700}
                fontFamily="Manrope"
                sx={{ whiteSpace: "pre-line" }}
              >
                {isComplete
                  ? translate("onboarding.complete.leftTitle", { defaultValue: "Setup complete!" })
                  : translate("onboarding.left.title")
                }
              </Typography>
            </Box>
            <Box sx={getAnimationStyle(3)}>
              <Typography
                fontSize="18.8px"
                lineHeight="28.2px"
                fontWeight={800}
                maxWidth="560px"
                fontFamily="Manrope"
                sx={{ opacity: 0.9 }}
              >
                {isComplete
                  ? translate("onboarding.complete.leftSubtitle", { defaultValue: "You're all set to start managing every move in one platform." })
                  : translate("onboarding.left.subtitle")
                }
              </Typography>
            </Box>
          </Stack>
        )}

        <Box
          sx={{
            mt: isCompact ? "auto" : 0,
            mb: isCompact ? 4 : 0,
            ...getAnimationStyle(4),
            width: "100%",
          }}
        >
          <Stack spacing={GAP_UNITS}>
            {steps.map((step, index) => (
              <StepIndicator
                key={step.number}
                {...step}
                isLast={index === steps.length - 1}
                onClick={() =>
                  (step.completed || step.active) && onStepClick?.(step.number)
                }
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default LeftSideOnboarding;
